const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { skipAuth, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Add CSRF header for admin routes
  if (endpoint.startsWith('/admin/') && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(fetchOptions.method || 'GET')) {
    (headers as Record<string, string>)['X-Requested-With'] = 'XMLHttpRequest';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed');
  }

  return response.json();
}

// Public API
export const api = {
  // Categories & Services
  getCategories: () => request<{ categories: import('../types').Category[] }>('/categories'),
  getCategory: (slug: string) => request<{ category: import('../types').Category; services: import('../types').Service[] }>(`/categories/${slug}`),
  getServices: (category?: string) => {
    const params = category ? `?category=${category}` : '';
    return request<{ services: import('../types').Service[] }>(`/services${params}`);
  },
  getService: (slug: string) => request<{ service: import('../types').Service }>(`/services/${slug}`),

  // Projects
  getProjects: (params?: { type?: string; city?: string; featured?: boolean; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.city) searchParams.set('city', params.city);
    if (params?.featured) searchParams.set('featured', 'true');
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ projects: import('../types').Project[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/projects${query ? `?${query}` : ''}`);
  },
  getProject: (slug: string) => request<{ project: import('../types').Project }>(`/projects/${slug}`),
  getFeaturedProjects: (limit = 6) => request<{ projects: import('../types').Project[] }>(`/projects/featured/list?limit=${limit}`),
  getCities: () => request<{ cities: string[] }>('/projects/cities'),

  // Availability
  getSlots: (date: string, serviceId: string, timezone: string) =>
    request<{ date: string; timezone: string; slots: import('../types').TimeSlot[] }>(`/availability/slots?date=${date}&service_id=${serviceId}&timezone=${timezone}`),
  getDates: (serviceId: string, timezone: string, days = 30) =>
    request<{ dates: { date: string; hasSlots: boolean }[] }>(`/availability/dates?service_id=${serviceId}&timezone=${timezone}&days=${days}`),
  getTimezone: () => request<{ timezone: string; buffer_minutes: number }>('/availability/timezone'),

  // Appointments
  createAppointment: (data: import('../types').BookingFormData) =>
    request<{ appointment_id: string; razorpay_order_id: string; amount: number; currency: string; key_id: string }>('/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createDirectAppointment: (data: import('../types').BookingFormData) =>
    request<{ success: boolean; appointment_id: string; message: string }>('/appointments/direct', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getAppointment: (id: string) => request<{ appointment: import('../types').Appointment }>(`/appointments/${id}`),

  // Payments
  verifyPayment: (data: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) =>
    request<{ success: boolean; appointment_id: string }>('/payments/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Content
  getContent: (id: string) => request<{ block: import('../types').ContentBlock }>(`/content/${id}`),
  getPublicSettings: () => request<{ settings: Record<string, string> }>('/settings/public'),

  // Blog
  getBlogPosts: (params?: { page?: number; limit?: number; category?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.category) searchParams.set('category', params.category);
    const query = searchParams.toString();
    return request<{ posts: import('../types').BlogPost[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/blog${query ? `?${query}` : ''}`);
  },
  getBlogPost: (slug: string) =>
    request<{ post: import('../types').BlogPost; comments: import('../types').BlogComment[] }>(`/blog/${slug}`),
  getBlogCategories: () =>
    request<{ categories: { category: string; count: number }[] }>('/blog/categories'),
  submitComment: (slug: string, data: { author_name: string; author_email: string; content: string; parent_id?: string }) =>
    request<{ success: boolean; message: string }>(`/blog/${slug}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Newsletter
  subscribeNewsletter: (data: { email: string; name?: string; source?: string }) =>
    request<{ success: boolean; message: string }>('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Contact
  submitContact: (data: { name: string; email: string; phone?: string; subject: string; message: string }) =>
    request<{ success: boolean; message: string }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Auth API
export const authApi = {
  login: (data: import('../types').LoginFormData) =>
    request<{ success: boolean; admin: import('../types').Admin }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  logout: () => request<{ success: boolean }>('/auth/logout', { method: 'POST' }),
  me: () => request<{ admin: import('../types').Admin }>('/auth/me'),
  status: () => request<{ authenticated: boolean; admin?: import('../types').Admin }>('/auth/status'),
};

// Admin API
export const adminApi = {
  // Dashboard
  getDashboard: () => request<import('../types').DashboardStats>('/admin/dashboard'),

  // Categories
  getCategories: () => request<{ categories: import('../types').Category[] }>('/admin/categories'),
  createCategory: (data: Partial<import('../types').Category>) =>
    request<{ id: string }>('/admin/categories', { method: 'POST', body: JSON.stringify(data) }),
  updateCategory: (id: string, data: Partial<import('../types').Category>) =>
    request<{ success: boolean }>(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCategory: (id: string) =>
    request<{ success: boolean }>(`/admin/categories/${id}`, { method: 'DELETE' }),

  // Services
  getServices: () => request<{ services: import('../types').Service[] }>('/admin/services'),
  createService: (data: Partial<import('../types').Service>) =>
    request<{ id: string }>('/admin/services', { method: 'POST', body: JSON.stringify(data) }),
  updateService: (id: string, data: Partial<import('../types').Service>) =>
    request<{ success: boolean }>(`/admin/services/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteService: (id: string) =>
    request<{ success: boolean }>(`/admin/services/${id}`, { method: 'DELETE' }),

  // Projects
  getProjects: () => request<{ projects: import('../types').Project[] }>('/admin/projects'),
  createProject: (data: Partial<import('../types').Project>) =>
    request<{ id: string }>('/admin/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: string, data: Partial<import('../types').Project>) =>
    request<{ success: boolean }>(`/admin/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProject: (id: string) =>
    request<{ success: boolean }>(`/admin/projects/${id}`, { method: 'DELETE' }),
  addProjectImage: (projectId: string, data: { image_url: string; image_type: string; alt_text?: string }) =>
    request<{ id: string }>(`/admin/projects/${projectId}/images`, { method: 'POST', body: JSON.stringify(data) }),
  deleteProjectImage: (projectId: string, imageId: string) =>
    request<{ success: boolean }>(`/admin/projects/${projectId}/images/${imageId}`, { method: 'DELETE' }),

  // Availability
  getAvailability: () => request<{ rules: import('../types').AvailabilityRule[] }>('/admin/availability'),
  createAvailability: (data: Partial<import('../types').AvailabilityRule>) =>
    request<{ id: string }>('/admin/availability', { method: 'POST', body: JSON.stringify(data) }),
  updateAvailability: (id: string, data: Partial<import('../types').AvailabilityRule>) =>
    request<{ success: boolean }>(`/admin/availability/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAvailability: (id: string) =>
    request<{ success: boolean }>(`/admin/availability/${id}`, { method: 'DELETE' }),

  // Blackouts
  getBlackouts: () => request<{ blackouts: import('../types').BlackoutDate[] }>('/admin/blackouts'),
  createBlackout: (data: { date: string; reason?: string }) =>
    request<{ id: string }>('/admin/blackouts', { method: 'POST', body: JSON.stringify(data) }),
  deleteBlackout: (id: string) =>
    request<{ success: boolean }>(`/admin/blackouts/${id}`, { method: 'DELETE' }),

  // Appointments
  getAppointments: (params?: { status?: string; date?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.date) searchParams.set('date', params.date);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ appointments: import('../types').Appointment[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/appointments${query ? `?${query}` : ''}`);
  },
  updateAppointment: (id: string, data: { status: string; meeting_link?: string }) =>
    request<{ success: boolean }>(`/admin/appointments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Content
  getContent: () => request<{ blocks: import('../types').ContentBlock[] }>('/admin/content'),
  updateContent: (id: string, data: { title?: string; content: string; content_type?: string }) =>
    request<{ success: boolean }>(`/admin/content/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Settings
  getSettings: () => request<{ settings: Record<string, string> }>('/admin/settings'),
  updateSettings: (data: Record<string, string>) =>
    request<{ success: boolean }>('/admin/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Upload
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/admin/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new ApiError(response.status, error.error);
    }

    return response.json() as Promise<{ url: string; key: string }>;
  },

  // Meeting Integration
  getMeetingStatus: () =>
    request<{ provider: 'zoom' | 'google' | null; configured: boolean }>('/admin/meetings/status'),
  testMeetingConnection: (provider: 'zoom' | 'google') =>
    request<{ success: boolean; message?: string; error?: string }>('/admin/meetings/test', {
      method: 'POST',
      body: JSON.stringify({ provider }),
    }),

  // Audit Log
  getAuditLog: (params?: { entity_type?: string; entity_id?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.entity_type) searchParams.set('entity_type', params.entity_type);
    if (params?.entity_id) searchParams.set('entity_id', params.entity_id);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ logs: unknown[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/audit-log${query ? `?${query}` : ''}`);
  },

  // Blog Management
  getBlogPosts: (params?: { status?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ posts: import('../types').BlogPost[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/blog${query ? `?${query}` : ''}`);
  },
  getBlogPost: (id: string) =>
    request<{ post: import('../types').BlogPost }>(`/admin/blog/${id}`),
  createBlogPost: (data: Partial<import('../types').BlogPost>) =>
    request<{ id: string }>('/admin/blog', { method: 'POST', body: JSON.stringify(data) }),
  updateBlogPost: (id: string, data: Partial<import('../types').BlogPost>) =>
    request<{ success: boolean }>(`/admin/blog/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBlogPost: (id: string) =>
    request<{ success: boolean }>(`/admin/blog/${id}`, { method: 'DELETE' }),

  // Comments Management
  getComments: (params?: { status?: string; post_id?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.post_id) searchParams.set('post_id', params.post_id);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ comments: import('../types').BlogComment[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/comments${query ? `?${query}` : ''}`);
  },
  updateComment: (id: string, data: { status: string }) =>
    request<{ success: boolean }>(`/admin/comments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteComment: (id: string) =>
    request<{ success: boolean }>(`/admin/comments/${id}`, { method: 'DELETE' }),

  // Newsletter Management
  getNewsletterSubscribers: (params?: { status?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ subscribers: import('../types').NewsletterSubscriber[]; stats: { total: number; subscribed: number; pending: number; unsubscribed: number }; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/newsletter${query ? `?${query}` : ''}`);
  },
  deleteNewsletterSubscriber: (id: string) =>
    request<{ success: boolean }>(`/admin/newsletter/${id}`, { method: 'DELETE' }),

  // Contact Management
  getContactSubmissions: (params?: { status?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return request<{ submissions: import('../types').ContactSubmission[]; stats: { total: number; new: number; read: number; responded: number; closed: number }; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/contacts${query ? `?${query}` : ''}`);
  },
  getContactSubmission: (id: string) =>
    request<{ submission: import('../types').ContactSubmission }>(`/admin/contacts/${id}`),
  updateContactSubmission: (id: string, data: { status?: string; admin_notes?: string }) =>
    request<{ success: boolean }>(`/admin/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteContactSubmission: (id: string) =>
    request<{ success: boolean }>(`/admin/contacts/${id}`, { method: 'DELETE' }),
};

export { ApiError };
